import React, { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { OfferDisplayStatus, OfferItem } from '../types';
import { getOfferDisplayStatus, withEffectiveOfferStatus } from '../types';
import { useOffer, useOffersList, type OfferPerspective } from '../../../../api/offers';
import { isStripeAccountReady, useStripeAccountStatus } from '../../../../api/stripe';
import { useNow } from '../../../../hooks/useNow';
import { StripeOnboardingPromptModal } from './StripeOnboardingPromptModal';
import { FiPlus, FiChevronRight, FiBriefcase, FiFilter } from 'react-icons/fi';
import { StatusTip } from '../../../ui/Tooltip';
import { EmptyState } from '../../../ui/EmptyState';
import { offerStatusTips } from '../../../../onboarding/copy';
import { TOUR, tourTarget } from '../../../../onboarding/targets';

type SubFilter =
  | 'all'
  | 'draft'
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'changes_requested'
  | 'withdrawn'
  | 'expired';

function displayBadge(
  status: OfferDisplayStatus,
  perspective: OfferPerspective,
  options?: { paymentDue?: boolean },
) {
  switch (status) {
    case 'accepted':
      if (options?.paymentDue) {
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2a2010] px-3 py-1 text-xs font-semibold text-[#fbbf24] border border-[#4a3b1c]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" /> Payment due
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d2818] px-3 py-1 text-xs font-semibold text-[#4ade80] border border-[#1b432a]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> Offer accepted
        </span>
      );
    case 'changes_requested':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d2a33] px-3 py-1 text-xs font-semibold text-[#22d3ee] border border-[#164e63]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#06b6d4]" /> In negotiation
        </span>
      );
    case 'in_progress':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#182234] px-3 py-1 text-xs font-semibold text-[#60a5fa] border border-[#253956]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#3b82f6]" /> In progress
        </span>
      );
    case 'awaiting_funding':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2a2010] px-3 py-1 text-xs font-semibold text-[#fbbf24] border border-[#4a3b1c]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" /> Awaiting funding
        </span>
      );
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d2818] px-3 py-1 text-xs font-semibold text-[#4ade80] border border-[#1b432a]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> Completed
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2d1416] px-3 py-1 text-xs font-semibold text-[#f87171] border border-[#4c1d24]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#ef4444]" /> Declined
        </span>
      );
    case 'withdrawn':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3 py-1 text-xs font-semibold text-zinc-300 border border-zinc-700">
          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> Withdrawn
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3 py-1 text-xs font-semibold text-zinc-300 border border-zinc-700">
          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> Expired
        </span>
      );
    case 'pending':
    case 'draft':
    default:
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#182234] px-3 py-1 text-xs font-semibold text-[#60a5fa] border border-[#253956]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#3b82f6]" />{' '}
          {status === 'draft'
            ? 'Draft'
            : perspective === 'received'
              ? 'Awaiting Review'
              : 'Offer sent'}
        </span>
      );
  }
}

function OfferRow({
  offer,
  perspective,
  onOpen,
}: {
  offer: OfferItem;
  perspective: OfferPerspective;
  onOpen: () => void;
}) {
  // The list endpoint omits payment fields, so accepted rows read them from the detail query.
  const { data: detail } = useOffer(offer.id, { enabled: offer.status === 'accepted' });
  const source = detail ?? offer;
  const display = getOfferDisplayStatus(
    offer.status,
    source.paymentStatus,
    perspective === 'received',
    {
      amountPaid: source.amountPaid,
      remainingBalance: source.remainingBalance,
      milestones: source.milestones,
    },
  );
  const paymentDue =
    !source.gigUuid &&
    perspective === 'received' &&
    display === 'accepted' &&
    source.paymentStatus === 'unpaid';
  const isCreated = perspective === 'created';
  const label = isCreated
    ? offer.recipientUsername || offer.recipientName || offer.recipientEmail || 'Recipient'
    : offer.creatorName || 'Creator';
  const role = isCreated ? offer.recipientRole : offer.creatorRole;
  const subtitle = isCreated
    ? role || offer.recipientEmail || offer.recipientName || '—'
    : role || 'From creator';

  return (
    <div
      onClick={onOpen}
      className="group relative flex items-start sm:items-center gap-3 sm:gap-4 rounded-2xl border border-[#22242a] bg-[#121316] p-4 sm:p-5 shadow-sm transition hover:border-zinc-700 hover:bg-[#18191d] cursor-pointer"
    >
      <div className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400 flex-shrink-0 group-hover:border-zinc-600 transition">
        <FiBriefcase size={20} />
      </div>

      <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="min-w-0 sm:w-44 lg:w-52 flex-shrink-0">
          <h4 className="text-sm font-bold text-white truncate group-hover:text-blue-400 transition">
            {label}
          </h4>
          <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{subtitle}</p>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm sm:text-base font-bold text-white truncate">{offer.title}</h3>
          <p className="mt-1 text-xs font-bold text-white">
            {offer.currency} {offer.price.toLocaleString('en-US')}
          </p>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-3 min-w-0">
          <div className="min-w-0">
            <StatusTip
              content={
                offerStatusTips[
                  paymentDue
                    ? 'payment_due'
                    : display === 'pending' && perspective === 'received'
                      ? 'awaiting_you'
                      : display
                ]
              }
            >
              {displayBadge(display, perspective, { paymentDue })}
            </StatusTip>
          </div>
          <FiChevronRight
            size={18}
            className="text-zinc-500 group-hover:text-white transition flex-shrink-0"
          />
        </div>
      </div>
    </div>
  );
}

export const OffersTab: React.FC = () => {
  const navigate = useNavigate();
  const [subFilter, setSubFilter] = useState<SubFilter>('all');
  const [perspective, setPerspective] = useState<OfferPerspective>('created');

  const { data, isLoading, isError, refetch, isFetching } = useOffersList({
    perspective,
    page: 1,
    page_size: 50,
  });

  const [showStripePrompt, setShowStripePrompt] = useState(false);
  const { data: stripeAccount, isLoading: stripeLoading } = useStripeAccountStatus();
  const now = useNow(60_000);

  const rawResults = data?.results;
  const allOffers = useMemo(
    () => (rawResults ?? []).map((o) => withEffectiveOfferStatus(o, now)),
    [rawResults, now],
  );
  const draftCount = allOffers.filter((o) => o.status === 'draft').length;
  const pendingCount = allOffers.filter((o) => o.status === 'pending').length;
  const acceptedCount = allOffers.filter((o) => o.status === 'accepted').length;
  const rejectedCount = allOffers.filter((o) => o.status === 'rejected').length;
  const changesRequestedCount = allOffers.filter((o) => o.status === 'changes_requested').length;
  const withdrawnCount = allOffers.filter((o) => o.status === 'withdrawn').length;
  const expiredCount = allOffers.filter((o) => o.status === 'expired').length;
  const allCount = data?.count ?? allOffers.length;

  const offers = useMemo(() => {
    if (subFilter === 'all') return allOffers;
    return allOffers.filter((o) => o.status === subFilter);
  }, [allOffers, subFilter]);

  const showLoading = isLoading && !data;
  const showError = isError && !data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 rounded-full bg-[#121316] p-1.5 border border-[#22242a] self-start">
            {(['created', 'received'] as OfferPerspective[]).map((p) => (
              <button
                key={p}
                onClick={() => setPerspective(p)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition cursor-pointer capitalize ${
                  perspective === p
                    ? 'bg-[#22242a] text-white shadow'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-full bg-[#121316] p-1.5 border border-[#22242a] w-full min-w-0 overflow-x-auto no-scrollbar">
            {(
              [
                ['all', 'All', allCount],
                ['draft', 'Draft', draftCount],
                [
                  'pending',
                  perspective === 'received' ? 'Awaiting Review' : 'Offer Sent',
                  pendingCount,
                ],
                ['accepted', 'Accepted', acceptedCount],
                ['rejected', 'Declined', rejectedCount],
                ['changes_requested', 'In Negotiation', changesRequestedCount],
                ['withdrawn', 'Withdrawn', withdrawnCount],
                ['expired', 'Expired', expiredCount],
              ] as const
            ).map(([key, label, count]) => (
              <button
                key={key}
                onClick={() => setSubFilter(key)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition cursor-pointer whitespace-nowrap ${
                  subFilter === key
                    ? 'bg-[#22242a] text-white shadow'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {label} <span className="text-[11px] opacity-70 ml-1">{count}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => {
            if (!stripeLoading && !isStripeAccountReady(stripeAccount)) {
              setShowStripePrompt(true);
              return;
            }
            navigate({ to: '/offers/create' });
          }}
          {...tourTarget(TOUR.myGigsCreateOffer)}
          className="flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer shadow-md self-stretch sm:self-auto"
        >
          <FiPlus size={16} /> Create Offer
        </button>
      </div>

      <div className="space-y-3 relative">
        {isFetching && data && (
          <p className="absolute -top-5 right-0 text-[10px] text-zinc-500">Refreshing…</p>
        )}
        {showLoading ? (
          <div className="rounded-3xl border border-[#22242a] p-12 text-center text-sm text-zinc-400">
            Loading offers…
          </div>
        ) : showError ? (
          <div className="rounded-3xl border border-dashed border-red-900/50 p-12 text-center">
            <h3 className="text-lg font-bold text-white mb-1">Couldn’t load offers</h3>
            <p className="text-sm text-zinc-500 mb-4">Please try again.</p>
            <button
              onClick={() => refetch()}
              className="rounded-full bg-white px-5 py-2 text-xs font-bold text-black cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : offers.length === 0 ? (
          subFilter !== 'all' && allOffers.length > 0 ? (
            <EmptyState
              size="compact"
              icon={<FiFilter size={18} />}
              title="No offers match this filter"
              description="Try a different status, or choose All to see every offer."
              className="rounded-3xl border border-dashed border-[#22242a]"
            />
          ) : (
            <EmptyState
              icon={<FiBriefcase size={22} />}
              title="No offers yet"
              description={
                perspective === 'created'
                  ? 'Offers you send to applicants appear here. Start one from the Applicants tab, or use "Create Offer" above.'
                  : 'Offers that hirers send you appear here, ready for you to review and accept.'
              }
              action={
                perspective === 'created'
                  ? {
                      label: 'Go to Applicants',
                      onClick: () =>
                        navigate({
                          to: '/profile/my-gigs',
                          search: { tab: 'applicants' },
                          replace: true,
                        }),
                    }
                  : undefined
              }
              tourId="hiring"
              className="rounded-3xl border border-dashed border-[#22242a]"
            />
          )
        ) : (
          offers.map((off) => (
            <OfferRow
              key={off.id}
              offer={off}
              perspective={perspective}
              onOpen={() => navigate({ to: '/offers/$offerId', params: { offerId: off.id } })}
            />
          ))
        )}
      </div>

      <StripeOnboardingPromptModal
        isOpen={showStripePrompt}
        onClose={() => setShowStripePrompt(false)}
      />
    </div>
  );
};
